<script setup lang="ts">
import type { IUserInfoExtended } from '~/interfaces/dto/auth/response';
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';

const props = withDefaults(defineProps<Partial<IUserInfoExtended>>(), {
  username: '',
  firstname: '',
  lastname: '',
  dateOfBirth: '',
});

const { $t, $trackEvent, $dayjs } = useNuxtApp();
const { $authService } = useServices();
const siteStore = useSiteStore();

const emits = defineEmits<{ (e: 'update'): void; (e: 'empty-schema'): void }>();

const form = ref<IFormBuilderReference>();
const errorCode = ref<string>('');
const errorRef = ref<string>('');
const userDetails = ref<Partial<IUserInfoExtended>>({
  username: props.username,
  firstname: props.firstname,
  lastname: props.lastname,
});

const isFirstNameDirty = computed(
  () => userDetails.value.firstname !== props.firstname,
);
const isLastNameDirty = computed(
  () => userDetails.value.lastname !== props.lastname,
);
const isValid = computed(
  () =>
    form.value.validated &&
    !form.value.invalid &&
    (!!userDetails.value.username ||
      !!userDetails.value.firstname ||
      !!userDetails.value.lastname) &&
    (isFirstNameDirty.value || isLastNameDirty.value),
);

const minAge = new Date();
minAge.setFullYear(minAge.getFullYear() - 18);
const maxAge = new Date();
maxAge.setFullYear(maxAge.getFullYear() - 101);
const normalizeName = (name: string): string => {
  return name
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

const updateDetailsHandler = async () => {
  if (!isValid.value) {
    errorCode.value = 'form-invalid';
    return;
  }
  try {
    errorCode.value = '';
    errorRef.value = '';
    $dayjs.tz.setDefault();
    const requestBody = {
      ...(!!userDetails.value.firstname && {
        firstname: normalizeName(userDetails.value.firstname),
      }),
      ...(!!userDetails.value.lastname && {
        lastname: normalizeName(userDetails.value.lastname),
      }),
      username: userDetails.value.username,
    };
    const data = await $authService.editUser(requestBody);
    if (data?.isSuccessful) {
      siteStore.toggleSiteNotice({
        severity: 'success',
        heading: $t('success'),
        content: { data: $t(`pers-details-updated-succ`) },
        timeout: 3000,
        enabled: true,
      });
      emits('update');
      await $trackEvent('Account', 'MY_ACCOUNT_DETAILS_UPDATE_ACTION');
    } else {
      errorCode.value = data?.error?.displayCode + '';
      errorRef.value = data?.error?.code + '';
    }
  } catch (e) {
    errorCode.value = e?.error?.displayCode || e?.error?.code;
    errorRef.value = '';
  }
};
</script>

<template>
  <div
    v-show="!!form && form.loaded"
    class="pt-2 px-4 pb-4 border-t stacked-border-color"
  >
    <p class="pb-3 text-base font-bold mt-4">
      {{ $t('add-personal-details') }}
    </p>
    <UserInterfaceDynamicFormBuilder
      ref="form"
      v-model="userDetails"
      :should-auto-focus="false"
      template-name="add personal details"
      @empty-schema="emits('empty-schema')"
    >
      <template #form-actions>
        <UserInterfaceGenericError
          v-if="errorCode"
          state="danger"
          class="max-w-72 mx-auto mb-2"
          :reference-code="errorRef"
        >
          {{ $t(`update-details-${errorCode}`) }}
        </UserInterfaceGenericError>
        <DesignSystemButton
          class="w-full justify-center mt-3"
          rounding="lg"
          :disabled="!isValid"
          @click="updateDetailsHandler"
        >
          {{ $t('update') }}
        </DesignSystemButton>
      </template>
    </UserInterfaceDynamicFormBuilder>
  </div>
</template>
